const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: '$',
  eur: '€',
  gbp: '£',
  inr: '₹',
  jpy: '¥',
  cad: 'CA$',
  aud: 'A$',
};

/** Format major-unit invoice amounts (same convention as InvoiceListTable). */
export function formatInvoiceMoney(amount: number, currency: string = 'usd'): string {
  const c = (currency || 'usd').toLowerCase();
  const symbol = CURRENCY_SYMBOLS[c] ?? `${c.toUpperCase()} `;
  if (!Number.isFinite(amount)) return '—';
  const hasCents = Math.abs(amount % 1) > 0;
  return `${symbol}${amount.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/** collection_rate: treat 0–1 as fraction, >1 as already-percent. */
export function formatCollectionRate(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  const pct = rate <= 1 ? rate * 100 : rate;
  return `${pct.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Scope-aware KPI count line. Never lump checkout into “invoices”. */
export function formatAnalyticsCountLine(
  source: 'all' | 'invoice' | 'checkout',
  kpis: { invoice_count: number; checkout_count?: number; billing_user_count: number }
): string {
  const invoices = kpis.invoice_count ?? 0;
  const checkouts = kpis.checkout_count ?? 0;
  const users = kpis.billing_user_count ?? 0;
  const parts: string[] = [];

  if (source === 'all') {
    parts.push(plural(invoices, 'invoice', 'invoices'));
    parts.push(plural(checkouts, 'checkout', 'checkouts'));
  } else if (source === 'checkout') {
    parts.push(plural(checkouts, 'checkout', 'checkouts'));
  } else {
    parts.push(plural(invoices, 'invoice', 'invoices'));
  }

  parts.push(plural(users, 'billing user', 'billing users'));
  return parts.join(' · ');
}

export function isAnalyticsPeriodEmpty(
  source: 'all' | 'invoice' | 'checkout',
  kpis: { invoice_count: number; checkout_count?: number; invoiced_amount?: number }
): boolean {
  const invoices = kpis.invoice_count ?? 0;
  const checkouts = kpis.checkout_count ?? 0;
  if (source === 'checkout') return checkouts === 0;
  if (source === 'invoice') return invoices === 0;
  return invoices === 0 && checkouts === 0;
}
